import { fetchWithAuth } from "./http";

// --- Types ---

export interface PartnerClient {
  customer_id: string;
  client_name: string;
  contact_email: string | null;
  project_count: number;
  portfolio_count: number;
  created_at: string | null;
}

export interface PartnerPortfolio {
  portfolio_id: string;
  customer_id: string;
  portfolio_name: string;
  description: string | null;
  project_count: number;
  created_at: string | null;
}

export interface PartnerClientDetail {
  customer_id: string;
  client_name: string;
  contact_email: string | null;
  project_count: number;
  portfolios: PartnerPortfolio[];
  created_at: string | null;
}

export type ProfilePrimarySource =
  | "metered"
  | "calibrated"
  | "estimated"
  | "gap_filled"
  | "mixed";

export interface ProjectWithLatest {
  project_id: string;
  project_name: string;
  eia_ba_code: string | null;
  latest_run_status: string | null;
  latest_run_id: string | null;
  // Generation-profile provenance (latest run). Generation is a primary
  // driver of results, so the list view shows how it was derived.
  profile_source_breakdown?: Record<string, number> | null;
  profile_primary_source?: ProfilePrimarySource | null;
  profile_metered_pct?: number | null;
}

// --- API calls ---

export const listClients = (token: string) =>
  fetchWithAuth<PartnerClient[]>("/partner/clients", token);

export function createClient(
  data: { client_name: string; contact_email?: string; portfolio_name?: string },
  token: string
) {
  return fetchWithAuth<PartnerClient>("/partner/clients", token, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export const getClient = (clientId: string, token: string) =>
  fetchWithAuth<PartnerClientDetail>(`/partner/clients/${clientId}`, token);

export const listClientProjects = (clientId: string, token: string) =>
  fetchWithAuth<ProjectWithLatest[]>(`/partner/clients/${clientId}/projects`, token);

export const listClientRuns = (clientId: string, token: string) =>
  fetchWithAuth<any[]>(`/partner/clients/${clientId}/runs`, token);

export const listClientPortfolios = (clientId: string, token: string) =>
  fetchWithAuth<PartnerPortfolio[]>(`/partner/clients/${clientId}/portfolios`, token);

export function createClientPortfolio(
  clientId: string,
  data: { portfolio_name: string; description?: string },
  token: string
) {
  return fetchWithAuth<PartnerPortfolio>(`/partner/clients/${clientId}/portfolios`, token, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function createClientProject(
  clientId: string,
  payload: Record<string, unknown>,
  token: string
) {
  return fetchWithAuth<{ project_id: string }>(`/partner/clients/${clientId}/projects`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export const listOwnProjects = (token: string) =>
  fetchWithAuth<ProjectWithLatest[]>("/partner/projects", token);

export function assignProjectsToClient(
  clientId: string,
  data: { project_ids: string[] },
  token: string
): Promise<{ moved: string[]; errors: Array<{ project_id: string; error: string }> }> {
  return fetchWithAuth(`/partner/clients/${clientId}/assign-projects`, token, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function requestUpgrade(
  clientId: string,
  data: { message?: string },
  token: string
) {
  return fetchWithAuth(`/partner/clients/${clientId}/request-upgrade`, token, {
    method: "POST",
    body: JSON.stringify(data),
  });
}
